import { useEffect, useRef, useState } from "react"
import { useBodyScrollLock } from "@/shared/lib/hooks/useBodyScrollLock"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { useNotifications } from "@/features/notifications/NotificationsProvider"
import type { ButtonLinkHeaderTone } from "@/lib/ButtonLink"
import {
  navbarHeaderIconButtonSize,
  navbarHeaderIconSize,
} from "@/widgets/layout/NavbarPartners"
import { BellBadge } from "@/widgets/notifications/BellBadge"
import { NotificationPopover } from "@/widgets/notifications/NotificationPopover"

type Props = {
  headerTone?: ButtonLinkHeaderTone
  /** Визуальный размер: default подстраивается под `navPillOutline`, compact — меньшая иконка для мобайла. */
  size?: "default" | "compact"
}

const MOBILE_BREAKPOINT = 640

export const NotificationBell = ({ headerTone = "solid", size = "default" }: Props) => {
  const { t } = useTranslation()
  const { unreadCount } = useNotifications()
  const [open, setOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  )
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onDocClick)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onDocClick)
    }
  }, [open])

  useBodyScrollLock(open && isMobile)

  const ariaLabel =
    unreadCount > 0
      ? t("notifications.bell.unreadAria", { count: unreadCount })
      : t("notifications.bell.aria")

  const iconTone =
    headerTone === "transparent"
      ? "text-white hover:bg-white/10"
      : "text-gray-700 hover:bg-gray-100 hover:text-brand-500"

  const sizeClass = size === "compact" ? "w-9 h-9" : navbarHeaderIconButtonSize
  const iconSize = size === "compact" ? "text-base" : navbarHeaderIconSize

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`relative ${sizeClass} flex items-center justify-center rounded-lg transition-colors ${iconTone} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`}
      >
        <i className={`ri-notification-3-line ${iconSize}`} aria-hidden />
        <BellBadge count={unreadCount} />
      </button>

      {open ? (
        isMobile ? (
          <div className="fixed inset-0 z-[60] flex items-end" role="presentation">
            <div aria-hidden className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div
              ref={popoverRef}
              role="dialog"
              aria-modal="true"
              aria-label={t("notifications.popover.title")}
              className="relative w-full bg-white rounded-t-2xl shadow-2xl border-t border-gray-100 flex flex-col max-h-[85vh]"
            >
              <NotificationPopover onClose={() => setOpen(false)} />
            </div>
          </div>
        ) : (
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={t("notifications.popover.title")}
            className="absolute right-0 top-full mt-2 w-[360px] sm:w-[380px] bg-white border border-gray-100 rounded-2xl shadow-xl z-[60] overflow-hidden"
          >
            <NotificationPopover onClose={() => setOpen(false)} />
          </div>
        )
      ) : null}
    </div>
  )
}
